import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Col,
  DatePicker,
  Form,
  Row,
  Tabs,
  notification,
} from 'antd';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { getIdMap } from 'ontraccr-common/lib/Common';
import { useDispatch, useSelector } from 'react-redux';

import HoverHelp from '../../../common/HoverHelp';
import OnTraccrSwitch from '../../../common/inputs/OnTraccrSwitch';
import colors from '../../../constants/Colors';

import Permissions from '../../../auth/Permissions';
import Analytics from '../../../helpers/Analytics';
import { currencyFormatter } from '../../../helpers/inputParsers';

import ProjectCurrencyInput from '../../ProjectCurrencyInput';
import { calculateBuildEstimateValues, hasDates } from '../../projectHelpers';
import { scheduleOfValuesData } from '../../ProjectScheduleOfValues/helpers';
import {
  getProjectScheduleOfValues,
  getScheduleOfValueSections,
  getSubContracts,
} from '../../state/projects.actions';

import ProjectAddPhaseCodeDrawer, { PHASE_ESTIMATE_NATIVE_KEYS } from '../ProjectAddPhaseCodeDrawer';
import BudgetTrackingPhasesTable from './BudgetTrackingPhasesTable';
import {
  calculateTotalMaterialCostUpdates,
  calculateTotalEquipmentCostUpdates,
  ESTIMATED_LABEL_STYLE,
  ESTIMATED_DISPLAY_TEXT_STYLE,
} from '../budgetTrackingHelpers';

import AddCostCodeDropdown from './AddCostCodeDropdown';
import BudgetTrackingCollapse from './BudgetTrackingCollapse';
import BudgetTrackingCostCodesTable from './BudgetTrackingCostCodesTable';
import BudgetTrackingEstimates from './BudgetTrackingEstimates';
import { isNullOrUndefined } from '../../../helpers/helpers';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import { getProjectIdMap } from '../../../state/selectors';
import ProjectBudgetTrackingImportDrawer from '../Import/ProjectBudgetTrackingImportDrawer';

export default function ProjectBudgetTrackingCreate({
  id: projectId,
  phases = [], // phases with attached costcodes
  costcodes = [], // phased costcodes
  unphasedCostcodes = [], // unphased costcodes
  projectEstimates = {},
  onAdd,
  onRemove,
  onEditCostcodeEstimateChanged,
  onEditPhaseCostCodes,
  onClearDates,
  onEditPhaseDetails,
  onApplyTemplate,
  templateView,
  divisionId,
  enableBuildEstimate,
  formRef,
  templateId,
  onCostcodeAdd,
  onRemoveCostcode,
  chosen = [], // costcodes that are already on the project
  onImportBudget,
}) {
  const dispatch = useDispatch();

  const {
    estimatedMaterialCost = null,
    estimatedOverheadCost = null,
    estimatedEquipmentCost = null,
    estimatedBurdenRate = null,
    estimatedSubContractTotalValue = null,
    contractValue = null,
    startDate = null,
    endDate = null,
    labourHours = null,
    labourCost = null,
  } = projectEstimates;

  const categories = useSelector((state) => state.costcodes.categories);
  const allCostUpdates = useSelector((state) => state.projects.projectCostUpdates);
  const scheduleOfValueSections = useSelector((state) => state.projects.scheduleOfValueSections);
  const scheduleOfValues = useSelector((state) => state.projects.scheduleOfValues);
  const hasPOPerms = Permissions.match('PURCHASE_ORDERS');
  const subcontracts = useSelector((state) => state.projects.subContracts);
  const customCategoryEstimates = useSelector((state) => state.projects.customCategoryEstimates);

  const projectCustomCategoryEstimates = useMemo(() => (
    customCategoryEstimates?.[projectId] ?? []
  ), [customCategoryEstimates, projectId]);

  const customCategories = useMemo(() => (
    Object.values(categories || {}).filter((category) => !category?.isDefault)
  ), [categories]);

  const customCategoriesMap = useMemo(() => getIdMap(customCategories), [customCategories]);

  const projectMap = useSelector(getProjectIdMap);

  const {
    totalBaseContractValue,
    holdbackPercentage,
  } = useMemo(() => {
    const currentProject = projectMap[projectId];
    return {
      totalBaseContractValue: currentProject
        ? currentProject.totalContractAmount
        : 0,
      holdbackPercentage: currentProject
        ? currentProject.holdbackPercentage
        : 0,
    };
  }, [projectMap, projectId]);

  const projectCostUpdates = useMemo(
    () => (allCostUpdates || []).filter((u) => u.projectId === projectId),
    [allCostUpdates, projectId],
  );

  const [useEstimates, setUseEstimates] = useState(
    labourHours
      || labourCost
      || startDate
      || endDate
      || estimatedMaterialCost
      || estimatedOverheadCost
      || estimatedEquipmentCost
      || estimatedSubContractTotalValue
      || estimatedBurdenRate
      || contractValue
      || templateView
      || projectCostUpdates?.some((u) => u.isBudgetUpdate && (u.type === 'material' || u.type === 'equipment'))
      || enableBuildEstimate
      || projectCustomCategoryEstimates?.length > 0,
  );
  const [useDates, setUseDates] = useState(
    hasDates(phases) || startDate || endDate,
  );
  const [useBuildEstimate, setUseBuildEstimate] = useState(enableBuildEstimate);
  const [selectedPhase, setSelectedPhase] = useState({});
  const [stateHours, setStateHours] = useState(labourHours);
  const [stateCosts, setStateCosts] = useState(labourCost);
  const [stateMaterialCost, setStateMaterialCost] = useState(estimatedMaterialCost);
  const [stateOverheadCost, setStateOverheadCost] = useState(estimatedOverheadCost);
  const [stateEquipmentCost, setStateEquipmentCost] = useState(estimatedEquipmentCost);
  const [stateSubContractTotalValue, setStateSubContractTotalValue] = useState(estimatedSubContractTotalValue);
  const [hasSetCustomEstimates, setHasSetCustomEstimates] = useState(false);
  const [stateCustomCategoryEstimates, setStateCustomCategoryEstimates] = useState({});
  const [prevEstimates, setPrevEstimates] = useState({});

  const [stateBurdenRate, setStateBurdenRate] = useState(estimatedBurdenRate);
  const [stateContractValue, setStateContractValue] = useState(contractValue);
  const [selectedCostCodes, setSelectedCostCodes] = useState([]);
  const [showTransferDrawer, setShowTransferDrawer] = useState();
  const [hoursExceeds, setHoursExceeds] = useState(false);
  const [newPhaseCode, setNewPhaseCode] = useState();
  const [newPhaseDescription, setNewPhaseDescription] = useState();
  const [selectedPhaseCostCodes, setSelectedPhaseCostCodes] = useState();
  const [importVisible, setImportVisible] = useState(false);

  const scheduleOfValueSectionMap = useMemo(() => (
    getIdMap(scheduleOfValueSections)
  ), [scheduleOfValueSections]);

  const contractTotalValue = useMemo(() => {
    if (!projectId) return stateContractValue;
    const { newLiveSummaryValue } = scheduleOfValuesData({
      projectId,
      templateId,
      totalBaseContractValue,
      holdbackPercentage,
      scheduleOfValues: scheduleOfValues ?? {},
      scheduleOfValueSectionMap,
    });

    const totalValue = newLiveSummaryValue.totalContractValue;

    return totalValue || stateContractValue;
  }, [
    projectId,
    templateId,
    totalBaseContractValue,
    holdbackPercentage,
    scheduleOfValues,
    scheduleOfValueSectionMap,
    stateContractValue,
  ]);

  const loadSubContracts = useCallback(async () => {
    if (!hasPOPerms || !projectId) return;
    dispatch(getSubContracts(projectId));
  }, [projectId, hasPOPerms]);

  const sumOfSubContracts = useMemo(() => (
    subcontracts.reduce((total, subcontract) => total + (subcontract.contractValue || 0), 0)
  ), [subcontracts]);

  const subContractTotalValue = useMemo(() => {
    if (
      (!subcontracts || subcontracts.length === 0)
      && !stateSubContractTotalValue
    ) return null;
    if (
      subcontracts?.length
      || stateSubContractTotalValue === null
      || useBuildEstimate
    ) {
      return sumOfSubContracts;
    }
    return stateSubContractTotalValue;
  }, [
    useBuildEstimate,
    subcontracts,
    stateSubContractTotalValue,
    sumOfSubContracts,
  ]);

  const categorizedCostcodeUpdateMap = useMemo(() => {
    const categorizedEstimates = projectCostUpdates.filter((update) => (
      update.costcodeId && update.isBudgetUpdate
    ));

    return categorizedEstimates.reduce((acc, update) => {
      const key = `${update?.phaseId ?? 'unphased'}.${update.costcodeId}`;
      if (!(key in acc)) {
        acc[key] = [];
      }
      acc[key].push(update);
      return acc;
    }, {});
  }, [projectCostUpdates]);

  const openAddDrawer = useCallback((index) => {
    const newSelectedPhase = index === 0
      ? { // Editing the new phase
        name: newPhaseCode,
        description: newPhaseDescription,
        costcodes: selectedCostCodes,
      } : { ...phases[index - 1] }; // Editing the existing phase

    setSelectedPhase(newSelectedPhase);
    setShowTransferDrawer(true);
    setSelectedPhaseCostCodes(newSelectedPhase.costcodes);
  }, [
    newPhaseCode,
    newPhaseDescription,
    selectedCostCodes,
    phases,
  ]);

  const addDrawerClosed = useCallback((value) => {
    setShowTransferDrawer(false);

    // If cancel, value == undefined. Else value == form values (and submitted is true on submit)
    if (value === undefined) {
      setSelectedPhaseCostCodes([]);
      return;
    }

    const phaseDetails = {
      name: value.phaseName,
      description: value.description,
    };
    PHASE_ESTIMATE_NATIVE_KEYS.forEach((key) => {
      if (value[key] != null) phaseDetails[key] = value[key];
    });
    Object.keys(value || {}).forEach((key) => {
      if (key.startsWith('phaseEstimate.') && value[key] != null) {
        phaseDetails[key] = value[key];
      }
    });

    if (selectedPhase.id !== undefined) {
      // Existing phase: update details and cost codes
      onEditPhaseDetails(selectedPhase.id, phaseDetails);
      onEditPhaseCostCodes(selectedPhase.id, selectedPhaseCostCodes);
    } else {
      // New phase: create phase with selected cost codes
      onAdd({
        ...phaseDetails,
        costcodes: selectedPhaseCostCodes,
      });
      setNewPhaseCode(null);
      setNewPhaseDescription(null);
      setSelectedCostCodes([]);
      setSelectedPhaseCostCodes([]);
    }
  }, [
    selectedPhaseCostCodes,
    selectedPhase,
    onEditPhaseDetails,
    onEditPhaseCostCodes,
    onAdd,
  ]);

  const onDateCheckChanged = useCallback(() => {
    const newUseDates = !useDates;
    setUseDates(newUseDates);
    Analytics.track(`Project/Dates/${newUseDates ? 'Enabled' : 'Disabled'}`);
    if (!newUseDates) {
      onClearDates();
    }
  }, [
    useDates,
    onClearDates,
  ]);

  const estimateFunctionMap = useMemo(() => ({
    estimatedLabourHours: setStateHours,
    estimatedLabourCost: setStateCosts,
    estimatedMaterialCost: setStateMaterialCost,
    estimatedOverheadCost: setStateOverheadCost,
    contractValue: setStateContractValue,
    estimatedBurdenRate: setStateBurdenRate,
    estimatedEquipmentCost: setStateEquipmentCost,
  }), []);

  const onImportClose = useCallback(() => setImportVisible(false), []);

  const onImport = useCallback((parsedData) => {
    const { estimates, hasDates: newHasDates } = parsedData;
    if (!useEstimates) setUseEstimates(true);
    if (!!useDates !== !!newHasDates) onDateCheckChanged();
    Object.keys(estimates).forEach((key) => {
      if (estimateFunctionMap[key]) estimateFunctionMap[key](estimates[key]);
    });
    return onImportBudget(parsedData);
  }, [useEstimates, useDates, onDateCheckChanged, estimateFunctionMap, onImportBudget]);

  const onImportExcel = useCallback(() => setImportVisible(true), []);

  const getAllocatedHours = useCallback(() => {
    const notificationKey = 'projectHoursExceed';

    if (!stateHours) return {};

    let totalHours = 0;

    unphasedCostcodes.forEach((cc) => {
      const { categoryAssignments } = cc;
      Object.values(categoryAssignments || {}).forEach((assignment) => {
        totalHours += assignment?.hours ?? 0;
      });
    });

    selectedCostCodes.forEach((cc) => {
      const { categoryAssignments } = cc;
      Object.values(categoryAssignments || {}).forEach((assignment) => {
        totalHours += assignment?.hours ?? 0;
      });
    });

    phases.forEach(({ costcodes: phasedCostcodes = [] }) => {
      phasedCostcodes.forEach((cc) => {
        const { categoryAssignments } = cc;
        Object.values(categoryAssignments || {}).forEach((assignment) => {
          totalHours += assignment?.hours ?? 0;
        });
      });
    });

    const newHoursExceeds = totalHours > stateHours;

    if (newHoursExceeds && !hoursExceeds && !useBuildEstimate) {
      notification.warn({
        key: notificationKey,
        message: 'Warning',
        description: 'You have allocated more hours than your top level estimate',
      });
      setHoursExceeds(newHoursExceeds);
    } else if (!newHoursExceeds && hoursExceeds) {
      notification.close(notificationKey);
      setHoursExceeds(newHoursExceeds);
    }

    return { totalHours, color: newHoursExceeds ? 'red' : colors.ONTRACCR_BLACK };
  }, [
    stateHours,
    unphasedCostcodes,
    selectedCostCodes,
    phases,
    hoursExceeds,
    setHoursExceeds,
    useBuildEstimate,
  ]);

  const onBuildEstimateToggledOff = () => {
    const {
      estimatedMaterialCost: oldMaterialCost,
      estimatedOverheadCost: oldOverheadCost,
      estimatedLabourHours: oldLabourHours,
      estimatedLabourCost: oldLabourCost,
      estimatedEquipmentCost: oldEquipmentCost,
      estimatedSubContractTotalValue: oldSubContractTotalValue,
    } = prevEstimates;

    setStateMaterialCost(oldMaterialCost);
    setStateOverheadCost(oldOverheadCost);
    setStateEquipmentCost(oldEquipmentCost);
    setStateHours(oldLabourHours);
    setStateCosts(oldLabourCost);
    formRef.current.setFieldsValue({
      estimatedMaterialCost: oldMaterialCost,
      estimatedOverheadCost: oldOverheadCost,
      estimatedLabourHours: oldLabourHours,
      estimatedLabourCost: oldLabourCost,
      estimatedEquipmentCost: oldEquipmentCost,
      estimatedSubContractTotalValue: oldSubContractTotalValue,
    });
  };

  const { totalHours: allocatedHours, color: allocatedColor } = getAllocatedHours();

  const showDateRange = useDates;

  const checkBoxes = (
    <Row gutter={12}>
      {!!useEstimates && (
        <Col>
          <Form.Item
            name="enableBuildEstimate"
            initialValue={enableBuildEstimate}
          >
            <OnTraccrSwitch
              onChange={() => {
                const newValue = !useBuildEstimate;
                setUseBuildEstimate(newValue);

                if (!newValue) { // toggled off
                  onBuildEstimateToggledOff();
                }
              }}
              title="Build Estimate?"
              style={{ marginBottom: 10 }}
              textStyle={{ ...ESTIMATED_LABEL_STYLE, marginBottom: 10 }}
            />
          </Form.Item>
        </Col>
      )}
      <Col>
        {!!useEstimates && (
          <OnTraccrSwitch
            onChange={onDateCheckChanged}
            value={useDates}
            title="Add Dates/Timelines?"
            textStyle={ESTIMATED_LABEL_STYLE}
          />
        )}
      </Col>
    </Row>
  );

  const estimatedBurdenCost = stateCosts && stateBurdenRate ? (stateCosts * stateBurdenRate) : 0;

  const updateValues = useCallback((setter) => (newValue) => {
    const floatValue = parseFloat(newValue);
    const safeValue = Number.isNaN(floatValue) ? 0 : floatValue;
    setter(safeValue);
  }, []);

  // Update the phase costcodes while maintaining existing estimate values
  const updateSelectedPhaseCostcodes = useCallback((value) => {
    const idMap = {};
    selectedPhaseCostCodes.forEach((costcode) => {
      const { id } = costcode;
      idMap[id] = costcode;
    });

    const newSelectedPhaseCostcodes = [];
    value.forEach((costcode) => {
      const { id } = costcode;
      if (idMap[id]) {
        newSelectedPhaseCostcodes.push(idMap[id]);
      } else {
        newSelectedPhaseCostcodes.push(costcode);
      }
    });
    setSelectedPhaseCostCodes(newSelectedPhaseCostcodes);
  }, [selectedPhaseCostCodes, setSelectedPhaseCostCodes]);

  useEffect(() => {
    if (useBuildEstimate) {
      const {
        buildLabourHours,
        buildLabourCost,
        buildMaterialCost,
        buildOverheadCost,
        buildEquipmentCost,
        buildCustomCategoryCosts,
      } = calculateBuildEstimateValues({
        unphasedCostcodes,
        phases,
        categories,
        customCategoriesMap,
      });

      const oldValues = formRef.current.getFieldsValue() ?? {};
      setPrevEstimates(oldValues);

      setStateMaterialCost(buildMaterialCost);
      setStateOverheadCost(buildOverheadCost);
      setStateEquipmentCost(buildEquipmentCost);
      setStateHours(buildLabourHours);
      setStateCosts(buildLabourCost);
      setStateCustomCategoryEstimates(buildCustomCategoryCosts);
      setStateSubContractTotalValue(subContractTotalValue);
      formRef.current.setFieldsValue({
        estimatedMaterialCost: buildMaterialCost,
        estimatedOverheadCost: buildOverheadCost,
        estimatedLabourHours: buildLabourHours,
        estimatedLabourCost: buildLabourCost,
        estimatedEquipmentCost: buildEquipmentCost,
        estimatedSubContractTotalValue: subContractTotalValue,
        ...buildCustomCategoryCosts,
      });
    }
  }, [
    useBuildEstimate,
    unphasedCostcodes,
    phases,
    subContractTotalValue,
    categories,
    customCategoriesMap,
  ]);

  const { unphasedWithAttachedCost, phasesWithAttachedCost } = useMemo(() => {
    const unphased = unphasedCostcodes.map((costcode) => ({
      ...costcode,
      costUpdates: categorizedCostcodeUpdateMap?.[`unphased.${costcode.id}`],
    }));

    const phasesWithCost = phases.map((phase) => {
      const { id: phaseId, costcodes: phaseCostcodes = [] } = phase;
      const costcodesWithAttachedCost = phaseCostcodes.map((costcode) => {
        const key = `${phaseId}.${costcode.id}`;
        return {
          ...costcode,
          costUpdates: categorizedCostcodeUpdateMap?.[key] ?? [],
        };
      });
      return {
        ...phase,
        costcodes: costcodesWithAttachedCost,
      };
    });

    return {
      unphasedWithAttachedCost: unphased,
      phasesWithAttachedCost: phasesWithCost,
    };
  }, [unphasedCostcodes, phases, categorizedCostcodeUpdateMap]);

  const totalMaterialCostUpdates = useMemo(
    () => calculateTotalMaterialCostUpdates(projectCostUpdates),
    [projectCostUpdates],
  );

  const totalEquipmentCostUpdates = useMemo(
    () => calculateTotalEquipmentCostUpdates(projectCostUpdates),
    [projectCostUpdates],
  );

  const materialCostTotal = (stateMaterialCost || 0) + totalMaterialCostUpdates;
  const equipmentCostTotal = (stateEquipmentCost || 0) + totalEquipmentCostUpdates;

  const customCategoryCostTotal = Object
    .values(stateCustomCategoryEstimates || {})
    .reduce((acc, curr) => acc + curr, 0);

  const expectedProfit = currencyFormatter(
    (contractTotalValue ?? 0)
    - (materialCostTotal ?? 0)
    - (stateOverheadCost ?? 0)
    - (stateCosts ?? 0)
    - (equipmentCostTotal ?? 0)
    - (estimatedBurdenCost ?? 0)
    - (subContractTotalValue ?? 0)
    - (customCategoryCostTotal ?? 0),
  );

  const totalEstimatedAmount = currencyFormatter(
    (materialCostTotal ?? 0)
    + (stateOverheadCost ?? 0)
    + (stateCosts ?? 0)
    + (equipmentCostTotal ?? 0)
    + (estimatedBurdenCost ?? 0)
    + (subContractTotalValue ?? 0)
    + (customCategoryCostTotal ?? 0),
  );

  useEffect(() => {
    dispatch(getProjectScheduleOfValues({
      projectId,
      templateId,
    }));
    dispatch(getScheduleOfValueSections({
      projectId,
      templateId,
    }));
  }, [projectId, templateId]);

  useEffect(() => {
    loadSubContracts();
  }, [loadSubContracts]);

  useEffect(() => {
    if (projectCustomCategoryEstimates?.length > 0 && !hasSetCustomEstimates && formRef.current) {
      const initialEstimates = {};
      const formValues = {};
      projectCustomCategoryEstimates.forEach(({ categoryId, value }) => {
        if (!isNullOrUndefined(value)) {
          initialEstimates[categoryId] = value;
          formValues[`categoryEstimate.${categoryId}`] = value;
        }
      });
      formRef.current.setFieldsValue(formValues);
      setStateCustomCategoryEstimates(initialEstimates);
      setHasSetCustomEstimates(true);
    }
  }, [projectCustomCategoryEstimates, hasSetCustomEstimates, formRef]);

  const estimatesToggle = useMemo(() => (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="budget-tracking-section-title">Estimates</span>
        <HoverHelp
          content={(
            <div style={{ width: 250 }}>
              Enabling estimates will allow you to add estimate accounts on all cost codes
            </div>
          )}
          iconProps={{ style: { color: colors.GRAY_600 } }}
        />
        <div onClick={(e) => e.stopPropagation()}>
          <OnTraccrSwitch
            value={useEstimates}
            onChange={setUseEstimates}
          />
        </div>
      </div>
      <OnTraccrButton
        title="Import"
        onClick={(e) => {
          e.stopPropagation();
          onImportExcel();
        }}
        style={{ marginRight: 20 }}
      />
    </div>
  ), [useEstimates, onImportExcel]);

  const customCategoryRows = useMemo(() => {
    if (!showDateRange && customCategories.length === 0) {
      return null;
    }

    const items = [...customCategories];
    if (showDateRange) items.push({ type: 'dateRange' });

    const categoryRows = [];
    let currentRow = [];
    items.forEach((item, index) => {
      currentRow.push(item);
      if (currentRow.length === 3 || index === items.length - 1) {
        categoryRows.push(currentRow);
        currentRow = [];
      }
    });

    return (
      <>
        {categoryRows.map((rowCategories, index) => (
          <Row gutter={[20, 20]} key={`row-${index}`}> {/* eslint-disable-line react/no-array-index-key */}
            {rowCategories.map((item) => {
              if (item?.type === 'dateRange') {
                return (
                  <Col span={8} key="dateRange">
                    <Form.Item
                      name="dateRange"
                      label="Start and End Date"
                      style={{ marginBottom: 0 }}
                    >
                      <DatePicker.RangePicker style={{ width: '100%' }} className="form-field-datetime-container" />
                    </Form.Item>
                  </Col>
                );
              }

              return (
                <Col span={8} key={item.id}>
                  <ProjectCurrencyInput
                    name={`categoryEstimate.${item.id}`}
                    title={`Estimated ${item.name} Cost`}
                    isNotDisplay={!useBuildEstimate}
                    onChange={updateValues((newValue) => {
                      setStateCustomCategoryEstimates((prev) => ({
                        ...prev,
                        [item.id]: newValue,
                      }));
                    })}
                    value={stateCustomCategoryEstimates[item.id]}
                    labelStyle={ESTIMATED_LABEL_STYLE}
                    displayStyle={ESTIMATED_DISPLAY_TEXT_STYLE}
                    displayAsDropdown
                  />
                </Col>
              );
            })}
          </Row>
        ))}
      </>
    );
  }, [
    showDateRange,
    customCategories,
    useBuildEstimate,
    startDate,
    endDate,
    updateValues,
    stateCustomCategoryEstimates,
  ]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          paddingBottom: 80,
        }}
      >
        {!templateView && (
          <BudgetTrackingEstimates
            header={estimatesToggle}
            useEstimates={useEstimates}
            isNotDisplay
            useBuildEstimate={useBuildEstimate}
            checkBoxes={checkBoxes}
            contractTotalValue={contractTotalValue}
            contractValue={stateContractValue}
            onContractValueChange={updateValues(setStateContractValue)}
            totalEstimatedAmount={totalEstimatedAmount}
            expectedProfit={expectedProfit}
            stateHours={stateHours}
            onHoursChange={updateValues(setStateHours)}
            stateCosts={stateCosts}
            labourCost={labourCost}
            onCostsChange={updateValues(setStateCosts)}
            materialCostTotal={materialCostTotal}
            onMaterialCostChange={updateValues(setStateMaterialCost)}
            stateOverheadCost={stateOverheadCost}
            estimatedOverheadCost={estimatedOverheadCost}
            onOverheadCostChange={updateValues(setStateOverheadCost)}
            equipmentCostTotal={equipmentCostTotal}
            onEquipmentCostChange={updateValues(setStateEquipmentCost)}
            stateBurdenRate={stateBurdenRate}
            onBurdenRateChange={updateValues(setStateBurdenRate)}
            estimatedBurdenCost={estimatedBurdenCost}
            subContractTotalValue={subContractTotalValue}
            onSubContractTotalValueChange={updateValues(setStateSubContractTotalValue)}
            customCategoryRows={customCategoryRows}
            allocatedHours={allocatedHours}
            allocatedColor={allocatedColor}
          />
        )}
        <BudgetTrackingCollapse
          panelKey="budget"
          header={<span className="budget-tracking-section-title">Budget</span>}
        >
          <Tabs defaultActiveKey="costCodes" style={{ marginTop: 10 }}>
            <Tabs.TabPane
              tab={<span className="budget-tracking-tab-title">Cost Codes</span>}
              key="costCodes"
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="budget-tracking-section-title">Cost Codes</span>
                <AddCostCodeDropdown
                  divisionId={divisionId}
                  onApplyTemplate={onApplyTemplate}
                  onCostcodeAdd={onCostcodeAdd}
                  chosen={chosen}
                  isTemplate={templateView}
                />
              </div>
              <BudgetTrackingCostCodesTable
                unphasedCostcodes={unphasedWithAttachedCost}
                isNotDisplay
                onEditCostcode={onEditCostcodeEstimateChanged}
                onRemoveCostcode={onRemoveCostcode}
                useBuildEstimate={useBuildEstimate}
              />
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={<span className="budget-tracking-tab-title">Phases</span>}
              key="phases"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 16,
                }}
              >
                <span className="budget-tracking-section-title">Phases</span>
                <OnTraccrButton
                  title="Add Phase"
                  style={{ height: 40 }}
                  onClick={() => openAddDrawer(0)}
                />
              </div>
              <BudgetTrackingPhasesTable
                dataSource={phasesWithAttachedCost}
                showEditButton
                showDeleteButton
                onEditPhaseDetails={onEditPhaseDetails}
                onEditPhaseCostCodes={onEditPhaseCostCodes}
                openAddDrawer={openAddDrawer}
                onRemove={onRemove}
                useBuildEstimate={useBuildEstimate}
                useDates={useDates}
                emptyText="No cost codes in this phase"
                isNotDisplay
              />
            </Tabs.TabPane>
          </Tabs>
        </BudgetTrackingCollapse>
      </div>
      <ProjectAddPhaseCodeDrawer
        onDrawerClosed={addDrawerClosed}
        onSelectedCostCodesChanged={updateSelectedPhaseCostcodes}
        visible={showTransferDrawer}
        selectedPhase={selectedPhase}
        costcodes={costcodes}
        projectCostcodes={chosen}
        useRedesign
      />
      <ProjectBudgetTrackingImportDrawer
        divisionId={divisionId}
        visible={importVisible}
        onClose={onImportClose}
        onImport={onImport}
      />
    </>
  );
}

/* eslint-disable react/forbid-prop-types */
ProjectBudgetTrackingCreate.propTypes = {
  id: PropTypes.string,
  phases: PropTypes.array,
  costcodes: PropTypes.array,
  projectEstimates: PropTypes.shape({
    estimatedMaterialCost: PropTypes.number,
    estimatedOverheadCost: PropTypes.number,
    estimatedEquipmentCost: PropTypes.number,
    estimatedBurdenRate: PropTypes.number,
    estimatedSubContractTotalValue: PropTypes.number,
    contractValue: PropTypes.number,
    startDate: PropTypes.instanceOf(DateTime),
    endDate: PropTypes.instanceOf(DateTime),
    labourHours: PropTypes.number,
    labourCost: PropTypes.number,
  }),
  unphasedCostcodes: PropTypes.array,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onEditCostcodeEstimateChanged: PropTypes.func.isRequired,
  onEditPhaseCostCodes: PropTypes.func.isRequired,
  onClearDates: PropTypes.func.isRequired,
  onEditPhaseDetails: PropTypes.func.isRequired,
  templateView: PropTypes.bool,
  divisionId: PropTypes.string.isRequired,
  enableBuildEstimate: PropTypes.bool,
  formRef: PropTypes.object.isRequired,
  templateId: PropTypes.string,
  onApplyTemplate: PropTypes.func.isRequired,
  onCostcodeAdd: PropTypes.func,
  onRemoveCostcode: PropTypes.func,
  chosen: PropTypes.array,
  onImportBudget: PropTypes.func,
};

ProjectBudgetTrackingCreate.defaultProps = {
  id: null,
  phases: [],
  costcodes: [],
  unphasedCostcodes: [],
  templateView: false,
  projectEstimates: {},
  enableBuildEstimate: false,
  templateId: null,
  onCostcodeAdd: () => {},
  onRemoveCostcode: null,
  chosen: [],
  onImportBudget: undefined,
};
